// Wires the auth seams to the real application.
//
// `provider-events.ts` deliberately imports neither the API client nor the
// wallet-connection UI: it is the policy (what a wallet event means for a
// session) and must stay testable without a network or an injected provider.
// This module is the other half — it supplies the real logout call and the
// real EIP-1193 provider, and it is imported only by `App.tsx`.
//
// ─── Why the logout is a bare fetch-through-apiCall with an explicit bearer ──
//
// `invalidate()` tears the local session down first, so nothing can read the
// token by the time the revoke runs. The token is handed in instead. Passing
// `bearer` explicitly also means this call cannot accidentally revoke a
// DIFFERENT session than the one being invalidated — if a login raced in
// between, the POST still names the session we ended.

import { apiCall } from "../api/client";
import { attachProviderEvents, setLogoutHandler } from "./provider-events";
import { getEvmProvider, type EvmProvider } from "./wallet";

/** The canonical logout. The only place either logout path posts to. */
export async function serverLogout(bearer: string | null): Promise<void> {
  if (bearer === null) return;
  await apiCall("/v2/admin/bff/logout", { method: "POST", bearer });
}

/** The injected provider, or null when the browser has no wallet.
 *
 *  `getEvmProvider()` throws when `window.ethereum` is absent — correct for a
 *  login attempt, wrong for a page load. A console opened in a browser with no
 *  wallet must still render; it simply has no events to subscribe to. */
export function optionalEvmProvider(): EvmProvider | null {
  try {
    return getEvmProvider();
  } catch {
    return null;
  }
}

/**
 * Install the auth runtime. Idempotent; returns a detach function.
 *
 * Called once from `App`. Not from `main.tsx`, so that a test rendering `<App/>`
 * exercises the same wiring the operator gets rather than a second, parallel
 * arrangement that could drift from it.
 */
export function installAuthRuntime(
  provider: EvmProvider | null = optionalEvmProvider(),
): () => void {
  setLogoutHandler(serverLogout);
  return attachProviderEvents(provider);
}
